/**
 * Conditions — Named condition evaluators for the .when() API.
 *
 * Each condition takes an ActionContext and returns true/false.
 * Used by AbilityBuilder.when(condition, modifier) for conditional
 * ability behavior at cast time.
 */

import type { ActionContext } from '../types';
import type { ConditionKey } from './types';
import { EcsCombatTargets } from '../../../combat/EcsCombatTargets';
import { ServiceRegistry } from '../../../../core/ServiceRegistry';
import { statusEffectManager } from '../../../combat/StatusEffectManager';

/** Minimal entity shape for condition evaluation. */
interface EntityLike {
  stats?: {
    hp?: number; health?: number; maxHp?: number; maxHealth?: number;
    mp?: number; mana?: number; maxMp?: number; maxMana?: number;
  };
  facing?: 'left' | 'right';
  position?: { x: number; y: number };
  isInvincible?: boolean;
  isFrozen?: boolean;
  isPoisoned?: boolean;
  isBurning?: boolean;
  isStunned?: boolean;
}

export const CONDITIONS: Record<ConditionKey, (ctx: ActionContext) => boolean> = {
  targetBelow50HP: (ctx) => {
    const target = getTargetEntity(ctx);
    if (!target?.stats || !Number.isFinite(target.stats.maxHp) || (target.stats.maxHp ?? 0) <= 0) return false;
    const hp = target.stats.hp ?? target.stats.health ?? 0;
    const maxHp = target.stats.maxHp ?? target.stats.maxHealth ?? 1;
    return hp > 0 && hp < maxHp * 0.5;
  },

  targetAbove50HP: (ctx) => {
    const target = getTargetEntity(ctx);
    if (!target?.stats || !Number.isFinite(target.stats.maxHp) || (target.stats.maxHp ?? 0) <= 0) return false;
    const hp = target.stats.hp ?? target.stats.health ?? 0;
    const maxHp = target.stats.maxHp ?? target.stats.maxHealth ?? 1;
    return hp >= maxHp * 0.5;
  },

  casterFullMana: (ctx) => {
    // player.mana/maxMana is the single source of truth (T1.1 unify) — the legacy
    // stats.mp copy drifted stale (toggle drain / spend only touched the mirror).
    const mana = ctx.currentPlayer.mana ?? 0;
    const maxMana = ctx.currentPlayer.maxMana ?? 1;
    return mana >= maxMana;
  },

  casterBelow30HP: (ctx) => {
    // player.health/maxHealth is the single source of truth (P3.1 unify) — the
    // legacy stats.hp copy drifted stale after damage.
    const hp = ctx.currentPlayer.health ?? 0;
    const maxHp = ctx.currentPlayer.maxHealth ?? 1;
    return hp < maxHp * 0.3;
  },

  targetFrozen: (ctx) => {
    return hasStatusFlag(ctx, 'isFrozen');
  },

  targetPoisoned: (ctx) => {
    return hasStatusFlag(ctx, 'isPoisoned');
  },

  targetBurning: (ctx) => {
    return hasStatusFlag(ctx, 'isBurning');
  },

  targetStunned: (ctx) => {
    return hasStatusFlag(ctx, 'isStunned');
  },

  casterHasShield: (ctx) => {
    return (ctx.currentPlayer as unknown as EntityLike).isInvincible === true;
  },

  behindTarget: (ctx) => {
    const target = getTargetEntity(ctx);
    if (!target?.facing) return false;
    const casterX = ctx.currentPlayer.position?.x ?? 0;
    const targetX = target.position?.x ?? 0;
    if (target.facing === 'right') return casterX < targetX;
    return casterX > targetX;
  },
};

// Register on globalThis for lazy circular-dep access from AbilityBuilder
import { setAbilityConditions } from '../../../../core/HelixGlobals';
setAbilityConditions(CONDITIONS);

function getTargetEntity(ctx: ActionContext): EntityLike | null {
  if (!ctx.targetId) return null;
  const target = new EcsCombatTargets().getTarget(ctx.targetId);
  if (!target) return null;
  const facing = ServiceRegistry.getInstance().get('EcsRegistry', { optional: true })
    ?.get(ctx.targetId)?.render?.facing;
  return {
    stats: { hp: target.health, maxHp: target.maxHealth },
    position: { x: target.x, y: target.y },
    facing: facing === 'left' || facing === 'right' ? facing : undefined,
    isFrozen: statusEffectManager.hasEffect(ctx.targetId, 'frozen'),
    isPoisoned: statusEffectManager.hasEffect(ctx.targetId, 'poison'),
    isBurning: statusEffectManager.hasEffect(ctx.targetId, 'burn'),
    isStunned: statusEffectManager.hasEffect(ctx.targetId, 'stun'),
  };
}

function hasStatusFlag(ctx: ActionContext, flag: keyof EntityLike): boolean {
  const target = getTargetEntity(ctx);
  if (!target) return false;
  return !!target[flag];
}
